/**
 * LiveEntry (/live) — the HOST's front door for a co-driven walkthrough. Mints a room
 * code, shows the shareable guest link, and drops the host into the shared workspace
 * (both people can click anything). The guest opens the link (/live/:code) with no
 * account.
 */
import { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { generateRoomCode, getHostRoom, getHostToken, guestLink, hostLogin } from "../demo/live/live";
import { useLiveRoom } from "../demo/live/LiveRoom";

export function LiveEntry() {
  const navigate = useNavigate();
  const { startRoom } = useLiveRoom();
  // Authenticated iff there's an unexpired host token in this tab. `authTick` lets a
  // successful login re-check without a reload.
  const [authTick, setAuthTick] = useState(0);
  const authed = useMemo(() => getHostToken() !== null, [authTick]);
  // Standing room from the bookmark (`&room=`) → the SAME guest link every time
  // (the one sitting in Cal.com booking emails). No room param → fresh ad-hoc code.
  const standingRoom = useMemo(() => getHostRoom(), []);
  const code = useMemo(() => standingRoom ?? generateRoomCode(), [standingRoom]);
  const link = guestLink(code);
  const [copied, setCopied] = useState(false);

  // Login form state
  const [password, setPassword] = useState("");
  const [loggingIn, setLoggingIn] = useState(false);
  const [loginError, setLoginError] = useState(false);

  const copy = () =>
    navigator.clipboard?.writeText(link).then(
      () => {
        setCopied(true);
        setTimeout(() => setCopied(false), 1800);
      },
      () => {},
    );

  const start = () => {
    startRoom(code, "host");
    navigate("/dashboard");
  };

  const submitLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!password || loggingIn) return;
    setLoggingIn(true);
    setLoginError(false);
    const ok = await hostLogin(password);
    setLoggingIn(false);
    if (ok) {
      setPassword("");
      setAuthTick((n) => n + 1); // re-render → authed
    } else {
      setLoginError(true);
    }
  };

  // Not logged in → password gate. The relay is the real enforcement; this is the UI.
  // Guests are unaffected (they use /live/:code and never see this).
  if (!authed) {
    return (
      <main className="mx-auto flex min-h-[calc(100dvh-2rem)] max-w-md flex-col items-center justify-center px-6 text-center">
        <p className="font-mono text-[11px] uppercase tracking-[0.28em] text-[var(--color-gold-soft)]">
          Psy180 · live guided demo
        </p>
        <h1 className="mt-4 font-heading text-3xl font-medium text-[var(--color-text-primary)]">
          Host sign-in
        </h1>
        <p className="mt-3 max-w-sm text-[14px] leading-relaxed text-[var(--color-text-muted)]">
          Hosting a live walkthrough is for the Psy180 team. Booked a demo? Open the link
          from your confirmation email instead.
        </p>
        <form onSubmit={submitLogin} className="mt-7 w-full">
          <input
            type="password"
            autoFocus
            value={password}
            onChange={(e) => { setPassword(e.target.value); setLoginError(false); }}
            placeholder="Host password"
            aria-label="Host password"
            className="w-full rounded-lg border border-[var(--color-border-strong)] bg-[var(--color-navy-mid)] px-4 py-3 text-center font-mono text-[14px] text-[var(--color-text-primary)] outline-none focus:border-[var(--color-gold)]"
          />
          {loginError && (
            <p className="mt-2 font-mono text-[11px] uppercase tracking-wide text-red-300">
              Wrong password — try again
            </p>
          )}
          <button
            type="submit"
            disabled={loggingIn || !password}
            className="mt-4 w-full rounded-lg bg-[var(--color-gold)] px-6 py-3 font-mono text-[12px] uppercase tracking-wide text-[var(--color-bg-deep)] transition-colors hover:bg-[var(--color-gold-soft)] disabled:opacity-40"
          >
            {loggingIn ? "Signing in…" : "Sign in to host →"}
          </button>
        </form>
        <a
          href="https://cal.com/psy180/15min"
          className="mt-6 font-mono text-[11px] uppercase tracking-wide text-[var(--color-text-ghost)] underline hover:text-[var(--color-gold-soft)]"
        >
          Not hosting? Book a walkthrough →
        </a>
      </main>
    );
  }

  return (
    <main className="mx-auto flex min-h-[calc(100dvh-2rem)] max-w-2xl flex-col items-center justify-center px-6 text-center">
      <p className="font-mono text-[11px] uppercase tracking-[0.28em] text-[var(--color-gold-soft)]">
        Psy180 · live guided demo
      </p>
      <h1 className="mt-4 font-heading text-3xl font-medium leading-tight text-[var(--color-text-primary)] sm:text-4xl">
        Walk someone through the demo — together, on video.
      </h1>
      <p className="mt-5 max-w-xl text-[15px] leading-relaxed text-[var(--color-text-muted)]">
        Send this link to the person you booked. When they open it you'll be face-to-face
        on video, sharing one screen — you can both click anything, and every click shows
        up on both screens. Everything is sample data; nothing is stored.
      </p>

      <div className="mt-8 w-full max-w-lg rounded-xl border border-[var(--color-border-gold)] bg-[var(--color-navy-mid)] p-4">
        <p className="font-mono text-[10px] uppercase tracking-[0.18em] text-[var(--color-text-ghost)]">
          {standingRoom ? "Standing guest link — the one in your booking emails" : "Guest link (one-off)"}
        </p>
        <div className="mt-2 flex items-center gap-2">
          <code className="flex-1 truncate rounded-md bg-[var(--color-bg-deep)] px-3 py-2 text-left font-mono text-[13px] text-[var(--color-text-primary)]">
            {link}
          </code>
          <button
            onClick={copy}
            className="shrink-0 rounded-md bg-[var(--color-gold)] px-3 py-2 font-mono text-[11px] uppercase tracking-wide text-[var(--color-bg-deep)] transition-colors hover:bg-[var(--color-gold-soft)]"
          >
            {copied ? "Copied ✓" : "Copy"}
          </button>
        </div>
      </div>

      <button
        onClick={start}
        className="mt-6 inline-flex items-center gap-2 rounded-lg bg-[var(--color-gold)] px-6 py-3 font-mono text-[12px] uppercase tracking-wide text-[var(--color-bg-deep)] transition-colors hover:bg-[var(--color-gold-soft)]"
      >
        Enter the demo as host →
      </button>
      <p className="mt-4 font-mono text-[10px] uppercase tracking-[0.16em] text-[var(--color-text-ghost)]">
        Keep this tab open · reloading ends the room
      </p>
    </main>
  );
}
